<script setup lang="ts">
import { Buffer } from 'buffer';

import { RadioGroup, RadioGroupLabel, RadioGroupOption } from '@headlessui/vue';
import { CheckIcon, CollectionIcon, PhotographIcon, CheckCircleIcon } from '@heroicons/vue/outline';
import { XCircleIcon } from '@heroicons/vue/solid';
import * as Sentry from '@sentry/vue';
import { useTimeoutPoll, useWebSocket } from '@vueuse/core';
import { bech32m } from 'bech32';
import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-vue-next';
import { ref } from 'vue';
import GobyCreateCollectionModal from '~/components/goby/GobyCreateCollectionModal.vue';
import { toHex } from '~/composables/useGoby';
import { cn } from '~/lib/utils';
import { isLocalData, type ImagesAndMetadata } from '~/types/minting';
import {
  initialMetadata,
  initialOnChainMetadata,
  type Collection,
  type SingleMintJob,
  type SingleNftData,
} from '~/types/store';
import { getApiRoot } from '~/utilities/api';
import { addCollectionToMetadata, FilebaseUploader } from '~/utilities/filebase-uploader';
import { isVideo } from '~/utilities/files';
import { createSecureTheBagRootSpend } from '~/utilities/nfts';

globalThis.Buffer = Buffer;

const route = useRoute();
const router = useRouter();

const config = useRuntimeConfig();

const gobyStore = useGobyStore();

type WizardState = 'type' | 'creator' | 'collection' | 'data' | 'details' | 'mint';
const wizardState = ref<WizardState>('type');
const mintType = ref<undefined | 'single' | 'edition' | 'series'>(undefined);

const confirmLegal = ref(false);
const nftData = ref<SingleNftData>({
  file: undefined,
  metadata: JSON.parse(JSON.stringify(initialMetadata)),
});
const offerPrice = ref<number | undefined>();
const offerRequestedTokenId = ref<string | undefined>();
const uploadOfferToDexie = ref<boolean | undefined>();
const editionTotal = ref<number | undefined>(1);

const onChainMetadata = ref<any>({ ...initialOnChainMetadata });
const currentFile = ref<any>(undefined);

const selectedDid = ref<any>(undefined);

const selectedCollection = ref<Collection | undefined>(undefined);
const showCreateCollectionModal = ref(false);
const newCollections = ref<Collection[]>([]);
const newEdition = ref<Collection>({
  id: crypto.randomUUID(),
  name: '',
  description: '',
  iconUrl: '',
  bannerUrl: '',
  attributes: [],
});

const showPaymentInfoModal = ref(false);
const paymentInfo = ref<{ country: string; tax_rate: number } | undefined>(undefined);

const shouldGroupEditionIntoCollection = ref<boolean | undefined>(undefined);
const selectedEditionOption = ref(undefined);

watch(wizardState, (value) => {
  router.push({ query: { ...route.query, step: value } });
});
watch(
  () => route.query,
  (query) => {
    // make sure the query.step is of the right type
    const queryStep = Array.isArray(query.step) ? query.step[0] : query.step;
    if (!queryStep || !['type', 'creator', 'collection', 'data', 'details', 'mint'].includes(queryStep)) {
      wizardState.value = 'type';
    } else {
      wizardState.value = queryStep as WizardState;
    }
  }
);

const getProgressWidth = (state: WizardState) => {
  switch (state) {
    case 'mint':
      return '100%';
    case 'details':
      return '70.3%';
    case 'data':
      return '50%';
    case 'collection':
      return '30.3%';
    case 'creator':
      return '2.5%';
    default:
      return '0';
  }
};

watch(
  newEdition,
  (value) => {
    if (value.name) {
      selectedCollection.value = value;
    }
    nftData.value.metadata.name = value.name;
    nftData.value.metadata.description = value.description;
  },
  { deep: true }
);

const addCollection = (collection: Collection) => {
  newCollections.value.push(collection);
  selectedCollection.value = collection;
};

const objectUrls = ref<string[]>([]);
function generateURL(file?: File) {
  console.log('generateURL', file);
  if (!file) {
    return undefined;
  }
  const fileSrc = URL.createObjectURL(file);
  if (!objectUrls.value.includes(fileSrc)) {
    objectUrls.value.push(fileSrc);
  }
  return fileSrc;
}

onUnmounted(() => {
  objectUrls.value.forEach((url) => {
    URL.revokeObjectURL(url);
  });
});

function getVideoCover(objectUrl: string, seekTo = 0.0) {
  return new Promise((resolve, reject) => {
    // load the file to a video player
    const videoPlayer = document.createElement('video');
    videoPlayer.setAttribute('src', objectUrl);
    videoPlayer.load();
    videoPlayer.addEventListener('error', (ex) => {
      reject('error when loading video file', ex);
    });
    // load metadata of the video to get video duration and dimensions
    videoPlayer.addEventListener('loadedmetadata', () => {
      // seek to user defined timestamp (in seconds) if possible
      if (videoPlayer.duration < seekTo) {
        reject('video is too short.');
        return;
      }
      // delay seeking or else 'seeked' event won't fire on Safari
      setTimeout(() => {
        videoPlayer.currentTime = seekTo;
      }, 200);
      // extract video thumbnail once seeking is complete
      videoPlayer.addEventListener('seeked', () => {
        // define a canvas to have the same dimension as the video
        const canvas = document.createElement('canvas');
        canvas.width = videoPlayer.videoWidth;
        canvas.height = videoPlayer.videoHeight;
        // draw the video frame to canvas
        const ctx = canvas.getContext('2d');
        ctx.drawImage(videoPlayer, 0, 0, canvas.width, canvas.height);
        // return the canvas image as a blob
        const dataURL = ctx.canvas.toDataURL('image/jpeg', 0.75);
        resolve(dataURL);
      });
    });
  });
}

const dropzone = ref();
const videoPreview = ref();
const loadAndSetFile = async (file: any) => {
  currentFile.value = file;
  currentFile.value.objectUrl = generateURL(file);
  videoPreview.value = undefined;
  if (isVideo(file.name)) {
    videoPreview.value = await getVideoCover(currentFile.value.objectUrl);
  }
};
const resetFile = () => {
  currentFile.value = undefined;
  videoPreview.value = undefined;
};

const licenseFromCollection = ref(false);
watch(
  selectedCollection,
  (value) => {
    if (value?.licenseUrl && value?.licenseHash) {
      onChainMetadata.value.licenseUrl = value.licenseUrl;
      onChainMetadata.value.licenseHash = value.licenseHash;
      licenseFromCollection.value = true;
    } else {
      onChainMetadata.value.licenseUrl = undefined;
      onChainMetadata.value.licenseHash = undefined;
      licenseFromCollection.value = false;
    }
  },
  { deep: true }
);

const isMintingEnabled = computed(() => {
  return currentFile.value || seriesData.value.length > 0;
});

const nftForPreview = computed(() => {
  if (confirmedNFT.value) {
    return confirmedNFT.value;
  }
  return {
    id: mintedNftId.value || '',
    encoded_id: mintedNftId.value || '',
    data: {
      metadata_json: {
        name: nftData.value?.metadata?.name,
      },
      edition_number: 1,
      edition_total: editionTotal.value,
    },
    thumbnail_uri: uploadedDataUrl.value || videoPreview.value || currentFile.value?.objectUrl,
    collection: selectedCollection.value,
    creator: selectedDid.value || { id: '', encoded_id: '' },
    owner_address: { id: '', encoded_id: gobyStore.account || 'xch10000' },
  };
});

const mintForPreview = computed(() => {
  return {
    id: mint.value?.id,
    mint_name: seriesMetadata.value.mint_name,
    mint_description: seriesMetadata.value.mint_description,
    mint_thumbnail_uri: seriesMetadata.value.mint_thumbnail_uri,
    unminted_nft_count: seriesData.value?.length,
    requested: { [offerRequestedTokenId.value || 'xch']: offerPrice.value || 0 },
    creator: selectedDid.value || { id: '', encoded_id: '' },
  };
});

const setPaymentInfoAndMint = ($event: { country: string; taxRate: number }) => {
  paymentInfo.value = { country: $event.country, tax_rate: $event.taxRate };
  uploadAndMintNft();
};

const collectionForPreview = computed(() => {
  if (confirmedCollection.value) {
    return {
      ...confirmedCollection.value,
      unminted_nft_count:
        selectedMintingType.value.id === 'on-demand' &&
        editionTotal.value &&
        editionTotal.value > confirmedCollection.value.unminted_nft_count
          ? editionTotal.value
          : confirmedCollection.value.unminted_nft_count,
    };
  }
  return {
    id: selectedCollection.value?.id,
    name: selectedCollection.value?.name,
    thumbnail_uri:
      selectedCollection.value?.iconUrl ||
      uploadedDataUrl.value ||
      currentFile.value?.objectUrl ||
      (seriesData.value.length > 0 && 'dataUris' in seriesData.value[0] && seriesData.value[0]?.dataUris[0]),
    creator: selectedDid.value,
  };
});

const mintingSteps = computed(() => [
  {
    name: 'Uploading files',
    description: `Uploading your data and metadata to IPFS`,
    status: uploadPending.value ? 'current' : 'complete',
  },
  {
    name: 'Signing transaction',
    description: 'Signing the transaction with your wallet.',
    status: mintedNftId.value || mint.value ? 'complete' : uploadPending.value ? 'upcoming' : 'current',
  },
  {
    name: 'Waiting for mempool confirmation',
    description: 'Waiting for the blockchain to confirm your transaction',
    status:
      mintPending.value || mintOffering.value ? 'current' : mintedNftId.value || mint.value ? 'complete' : 'upcoming',
  },
  {
    name: 'Completed!',
    description: `You can now open your ${mintType.value === 'edition' ? 'edition' : 'NFT'} by clicking the preview`,
    status: (mintedNftId.value || mint.value) && !(mintPending.value || mintOffering.value) ? 'complete' : 'upcoming',
  },
]);

const editionOptions = [
  { name: '10', value: 10 },
  { name: '25', value: 25 },
  { name: '50', value: 50 },
  { name: 'Custom' },
];

const groupEditionIntoCollectionOptions = [
  { name: 'Yes', value: true },
  { name: 'No', value: false },
];
watch(selectedEditionOption, (option) => {
  if (option?.name !== 'Custom') {
    editionTotal.value = option?.value;
  } else {
    editionTotal.value = undefined;
  }
});

const mintingTypes = [
  {
    id: 'on-demand',
    title: 'Mint on Demand',
    description: 'The NFTs are offered for sale and will be minted when bought',
    users: 'Fee: 10% of the sale price',
  },
  {
    disabled: false,
    id: 'direct',
    title: 'Direct Mint',
    description: 'The NFTs are minted directly into your wallet',
    users: 'Fee: 0.005 XCH per NFT',
  },
];
const selectedMintingType = ref(mintingTypes[0]);

const publishingMethods = [
  {
    id: 'private',
    title: 'Private',
    description: 'The mint offers are private and users have to request a random one on mint.',
  },
  {
    id: 'public',
    title: 'Public',
    description: 'The mint offers are published on MintGarden and Dexie, for users to pick and choose.',
  },
];
const selectedPublishingMethod = ref(publishingMethods[0]);

const mintingError = ref<string | undefined>();
const uploadPending = ref<boolean>(false);
const uploadProgress = ref<number>(0);
const mint = ref<any | undefined>();
const unwindJobId = ref<any | undefined>(13);
const unwindType = ref<string | undefined>();
const mintedNftId = ref<string | undefined>();
const uploadedDataUrl = ref<string | undefined>();
const mintPending = ref<boolean>(false);
const mintOffering = ref<boolean>(false);

watch(mint, (value) => {
  router.push({ query: { ...route.query, id: value?.id } });
});
watch(mintedNftId, (value) => {
  router.push({ query: { ...route.query, nft: value } });
});
const confirmedNFT = ref();
if (route.query.id) {
  try {
    const mintId = (Array.isArray(route.query.id) ? route.query.id[0] : route.query.id) || undefined;
    mint.value = await $fetch(`${getApiRoot()}/mint/${mintId}`);
    wizardState.value = 'mint';
    mintType.value = mint.value.mint_type === 1 ? 'series' : 'edition';
    mintPending.value = true;
    unwindJobId.value = mint.value.unwind_job_id;
    unwindType.value = mint.value.requested_mojos !== null ? 'offer' : 'mint';
  } catch (e) {
    router.replace({ query: { ...route.query, mint: undefined, step: undefined } });
  }
} else if (route.query.nft) {
  mintedNftId.value = (Array.isArray(route.query.nft) ? route.query.nft[0] : route.query.nft) || undefined;
  wizardState.value = 'mint';
  try {
    const nft = await $fetch(`${getApiRoot()}/nfts/${mintedNftId.value}`);
    confirmedNFT.value = nft;
    mintPending.value = !nft;
  } catch (e) {
    mintPending.value = true;
  }
} else {
  router.replace({ query: { ...route.query, step: undefined } });
}

const reset = () => {
  confirmLegal.value = false;
  nftData.value = {
    file: undefined,
    metadata: JSON.parse(JSON.stringify(initialMetadata)),
  };
  seriesMetadata.value = {
    mint_name: undefined,
    mint_description: undefined,
    mint_thumbnail_uri: undefined,
    release_time: undefined,
  };
  seriesData.value = [];
  offerPrice.value = undefined;
  uploadOfferToDexie.value = undefined;
  editionTotal.value = 1;

  shouldGroupEditionIntoCollection.value = undefined;
  selectedEditionOption.value = undefined;

  onChainMetadata.value = { ...initialOnChainMetadata };
  resetFile();

  selectedDid.value = undefined;

  selectedCollection.value = undefined;
  showCreateCollectionModal.value = false;
  newCollections.value = [];
  newEdition.value = {
    id: crypto.randomUUID(),
    name: '',
    description: '',
    iconUrl: '',
    bannerUrl: '',
    attributes: [],
  };

  paymentInfo.value = undefined;

  selectedMintingType.value = mintingTypes[0];
  mintingError.value = undefined;
  uploadPending.value = false;
  mint.value = undefined;
  mintedNftId.value = undefined;
  uploadedDataUrl.value = undefined;
  mintPending.value = false;
  mintOffering.value = false;
};

const setType = (type: 'single' | 'edition' | 'series') => {
  mintType.value = type;
  wizardState.value = 'creator';
  reset();
};

const seriesMetadata = ref<{
  mint_name: string | undefined;
  mint_description: string | undefined;
  mint_thumbnail_uri: string | undefined;
  release_time: Date | undefined;
}>({
  mint_name: undefined,
  mint_description: undefined,
  mint_thumbnail_uri: undefined,
  release_time: undefined,
});
const seriesData = ref<ImagesAndMetadata[]>([]);

watch(seriesData, (value) => {
  if (value.length > 0) {
    if ('metadataUris' in seriesData.value[0]) {
      fetch(seriesData.value[0].metadataUris[0])
        .then((response) => response.json())
        .then((data) => {
          if (data.collection) {
            selectedCollection.value = {
              id: data.collection.id,
              name: data.collection.name,
              description: data.collection.attributes?.find((attribute: any) => attribute.name === 'description')
                ?.value,
              iconUrl: data.collection.attributes?.find((attribute: any) => attribute.name === 'icon')?.value,
              attributes: [],
            };
          }
        });
    }
  }
});

const handleSeriesUpload = () => {
  if (seriesData.value.length === 0) {
    alert('No series data found. Please upload a directory with a valid metadata.csv file.');
    return;
  }

  wizardState.value = 'details';
};

const uploadAndMintNft = async () => {
  if (!isMintingEnabled.value) {
    return;
  }

  if (!confirmLegal.value) {
    return;
  }

  wizardState.value = 'mint';
  mintingError.value = undefined;
  mintPending.value = false;
  mintedNftId.value = undefined;
  uploadedDataUrl.value = undefined;
  uploadPending.value = true;
  try {
    const updatedMintJob: SingleMintJob = {
      did: selectedDid.value,
      collectionId: selectedCollection.value?.id,
      onChainMetadata: onChainMetadata.value,
      uploadOfferToDexie: uploadOfferToDexie.value,
      nfts: [],
    };

    if (mintType.value === 'series') {
      for (const item of seriesData.value) {
        if (isLocalData(item)) {
          throw new Error('Uploading data not yet supported for series minting');
        } else {
          updatedMintJob.nfts.push({
            urisAndHashes: {
              dataUris: item.dataUris,
              dataHash: item.dataHash,
              metadataUris: item.metadataUris,
              metadataHash: item.metadataHash,
            },
            metadata: undefined,
            onChainMetadata: {
              edition_number: item.onChainMetadata?.edition_number || 1,
              edition_total: item.onChainMetadata?.edition_total || 1,
            },
          });
        }
      }
    } else if (mintType.value === 'single') {
      updatedMintJob.nfts.push(
        Object.assign({}, nftData.value, {
          name: nftData.value?.metadata.name,
          file: currentFile.value || undefined,
          onChainMetadata: {
            edition_number: 1,
            edition_total: 1,
          },
        })
      );
    } else if (mintType.value === 'edition') {
      if (!editionTotal.value) {
        throw new Error('Please specify an edition total.');
      }

      for (let i = 1; i <= editionTotal.value; i++) {
        updatedMintJob.nfts.push(
          Object.assign({}, nftData.value, {
            name: nftData.value?.metadata.name,
            file: currentFile.value || undefined,
            onChainMetadata: {
              edition_number: i,
              edition_total: editionTotal.value,
            },
          })
        );
      }
    } else {
      throw new Error('Invalid mint type');
    }

    if (mintType.value !== 'single' && selectedMintingType.value.id === 'on-demand' && !offerPrice.value) {
      throw new Error('Please specify a sale price for on-demand minting');
    }

    if (updatedMintJob.onChainMetadata.licenseUrl && !updatedMintJob.onChainMetadata.licenseHash) {
      throw new Error('Missing license hash');
    }

    const nftsToUpload = updatedMintJob.nfts;

    const filesToUpload: any[] = [];
    for (const nftToUpload of nftsToUpload) {
      if (nftToUpload.urisAndHashes) {
        continue;
      }

      const file = nftToUpload.file;

      const metadataToUpload = nftToUpload.metadata;
      if (selectedCollection.value) {
        addCollectionToMetadata(metadataToUpload, selectedCollection.value);
      }
      const metadataString = JSON.stringify(metadataToUpload, null, 2);
      const metadataFile = new File([metadataString], 'metadata.json', {
        type: 'application/json',
      });

      if (!file) {
        console.error(`File not found: ${nftToUpload.file}`);
        return;
      }
      if (!filesToUpload.find((f) => f.name === file.name)) {
        filesToUpload.push(file);
      }
      if (!filesToUpload.find((f) => f.name === metadataFile.name)) {
        filesToUpload.push(metadataFile);
      }
    }

    console.info(`Uploading ${filesToUpload.length} files`);

    const filebaseUploader = new FilebaseUploader();
    uploadProgress.value = 0;
    const uploadedFiles = await filebaseUploader.upload(updatedMintJob.did.id, filesToUpload);
    uploadProgress.value = 1;
    console.info(`Done uploading ${filesToUpload.length} files`);
    uploadPending.value = false;

    if (mintType.value === 'single') {
      const nftToUpload = nftsToUpload[0];
      const dataUpload = uploadedFiles.find((file) => file.filename === nftToUpload.file?.name);
      const metadataUpload = uploadedFiles.find((file) => file.filename === 'metadata.json');
      if (dataUpload && metadataUpload) {
        uploadedDataUrl.value = dataUpload.uris[0];
        nftToUpload.urisAndHashes = {
          dataUris: dataUpload.uris,
          dataHash: dataUpload.hash,
          metadataUris: metadataUpload.uris,
          metadataHash: metadataUpload.hash,
        };
        const p2Puzzle = gobyStore.p2Puzzle;
        if (!p2Puzzle) {
          throw new Error('No p2 puzzle found');
        }
        mintedNftId.value = await gobyStore.mintNft(
          {
            ...nftToUpload.urisAndHashes,
            licenseHash: updatedMintJob.onChainMetadata.licenseHash || '',
            licenseUris: updatedMintJob.onChainMetadata.licenseUrl ? [updatedMintJob.onChainMetadata.licenseUrl] : [],
            editionNumber: 1,
            editionTotal: 1,
          },
          updatedMintJob.did.id,
          p2Puzzle.hashHex(),
          updatedMintJob.onChainMetadata.royaltyPercentage * 100
        );
        console.log('Waiting for NFT to be minted: ', mintedNftId.value);
        mintPending.value = true;
      }
    } else {
      const didCoin = (await gobyStore.getAssetCoins('did', selectedDid.value.id))[0];
      if (!didCoin) {
        throw new Error('DID coin not found');
      }

      const didFullPuzzle = Program.deserializeHex(didCoin.puzzle);
      const [, curriedArgs] = didFullPuzzle.uncurry()!;
      const innerPuzzle = curriedArgs[1];
      const [, innerCurriedArgs] = innerPuzzle.uncurry()!;
      const p2Puzzle = innerCurriedArgs[0];

      const targetPuzzleHash = updatedMintJob.onChainMetadata.targetAddress
        ? toHex(new Uint8Array(bech32m.fromWords(bech32m.decode(updatedMintJob.onChainMetadata.targetAddress).words)))
        : p2Puzzle.hashHex();

      const royaltyPuzzleHash = updatedMintJob.onChainMetadata.royaltyAddress
        ? toHex(new Uint8Array(bech32m.fromWords(bech32m.decode(updatedMintJob.onChainMetadata.royaltyAddress).words)))
        : p2Puzzle.hashHex();

      let requestedMojos: number | undefined;
      let requestedAssetId: string | undefined;
      if (selectedMintingType.value.id === 'on-demand') {
        requestedMojos = offerPrice.value
          ? offerPrice.value * (offerRequestedTokenId.value ? 10 ** 3 : 10 ** 12) //  CATs are 10^3 mojos
          : 0;
        if (offerRequestedTokenId.value && offerRequestedTokenId.value !== 'xch') {
          requestedAssetId = offerRequestedTokenId.value;
        }
      }
      const secureTheMintRequest = {
        mint_type: mintType.value === 'series' ? 1 : 0,
        mint_name: seriesMetadata.value?.mint_name,
        mint_description: seriesMetadata.value?.mint_description,
        mint_thumbnail_uri: seriesMetadata.value?.mint_thumbnail_uri,
        did_coin_id: didCoin.coinName,
        creator_id: updatedMintJob.did.id,
        metadata_list: [] as any[],
        target_puzzle_hash: targetPuzzleHash,
        requested_mojos: requestedMojos,
        requested_asset_id: requestedAssetId,
        royalty_puzzle_hash: royaltyPuzzleHash,
        royalty_percentage: updatedMintJob.onChainMetadata.royaltyPercentage,
        payment_info: paymentInfo.value,
        publish_offers: selectedPublishingMethod.value.id === 'public',
        release_time: seriesMetadata.value?.release_time,
      };
      for (const nftToUpload of nftsToUpload) {
        if (!nftToUpload.urisAndHashes) {
          const dataUpload = uploadedFiles.find((file) => file.filename === nftToUpload.file?.name);
          const metadataUpload = uploadedFiles.find((file) => file.filename === 'metadata.json');
          if (dataUpload && metadataUpload) {
            uploadedDataUrl.value = dataUpload.uris[0];
            nftToUpload.urisAndHashes = {
              dataUris: dataUpload.uris,
              dataHash: dataUpload.hash,
              metadataUris: metadataUpload.uris,
              metadataHash: metadataUpload.hash,
            };
          }
        }

        if (!nftToUpload.urisAndHashes) {
          throw new Error('No URIs and hashes found');
        }

        secureTheMintRequest.metadata_list.push({
          data_hash: nftToUpload.urisAndHashes.dataHash,
          data_uris: nftToUpload.urisAndHashes.dataUris,
          metadata_uris: nftToUpload.urisAndHashes.metadataUris,
          metadata_hash: nftToUpload.urisAndHashes.metadataHash,
          license_uris: updatedMintJob.onChainMetadata.licenseUrl ? [updatedMintJob.onChainMetadata.licenseUrl] : [],
          license_hash: updatedMintJob.onChainMetadata.licenseHash || '',
          edition_number: nftToUpload.onChainMetadata!.edition_number,
          edition_total: nftToUpload.onChainMetadata!.edition_total,
        });
      }

      const rootCoinAmount = nftsToUpload.length;

      const securedMint = await $fetch<any>(`${getApiRoot()}/mint/secure`, {
        method: 'post',
        body: secureTheMintRequest,
      });

      const prepend0xIfMissing = (hex: string) => (hex.startsWith('0x') ? hex : `0x${hex}`);

      const feePerNftInXCH = 0.005;
      const extraCoinsToCreate = [];
      if (selectedMintingType.value.id === 'direct') {
        const mintingPaymentPuzzleHash = new Uint8Array(
          bech32m.fromWords(bech32m.decode(securedMint.minting_payment_address).words)
        );
        extraCoinsToCreate.push({
          puzzleHash: mintingPaymentPuzzleHash,
          amount: nftsToUpload.length * feePerNftInXCH * 10 ** 12,
        });
      }
      const fee = 100_000_000;
      const requiredMojos = rootCoinAmount + fee + extraCoinsToCreate.reduce((acc, coin) => acc + coin.amount, 0);
      const xchCoins = await gobyStore.getAssetCoins(null, null, false, requiredMojos);

      const coinSpends = createSecureTheBagRootSpend(
        toRaw(didCoin),
        securedMint.root_puzzle,
        rootCoinAmount,
        xchCoins,
        extraCoinsToCreate,
        fee
      ).map((coinSpend) => ({
        coin: {
          parent_coin_info: prepend0xIfMissing(coinSpend.coin.parent_coin_info),
          puzzle_hash: prepend0xIfMissing(coinSpend.coin.puzzle_hash),
          amount: coinSpend.coin.amount,
        },
        puzzle_reveal: prepend0xIfMissing(coinSpend.puzzle_reveal),
        solution: prepend0xIfMissing(coinSpend.solution),
      }));

      // const { estimates } = await estimateCoinSpendsFee(coinSpends);
      // coinSpends = createSecureTheBagRootSpend(
      //   toRaw(didCoin),
      //   securedMint.root_puzzle,
      //   rootCoinAmount,
      //   p2Puzzle,
      //   xchCoins,
      //   [],
      //   estimates[1]
      // );

      const signature = await gobyStore.signCoinSpends(coinSpends);
      const spendBundle = { coin_spends: coinSpends, aggregated_signature: signature };

      console.log('Spend bundle: ', JSON.stringify(spendBundle, null, 2));

      const unwindMint = await $fetch<any>(`${getApiRoot()}/mint/unwind`, {
        method: 'post',
        body: {
          root_spend_bundle: spendBundle,
          genesis_coin_id: didCoin.coinName,
          mint_id: securedMint.id,
        },
      });

      // await sendTransaction(spendBundle);
      // useTrackEvent('wallet_mint_bulk');

      console.log('Waiting for all coins to be unwound: ', unwindMint.transaction_queue_job_id);
      mintPending.value = true;
      mint.value = securedMint;
      unwindJobId.value = unwindMint.transaction_queue_job_id;
      unwindType.value = unwindMint.unwind_type;
    }
  } catch (e: any) {
    Sentry.captureException(e);
    console.log(e);
    if (e?.data?.detail === 'Duplicate metadata entry') {
      mintingError.value = 'Duplicate metadata entry. Add an edition number for unique entries.';
      wizardState.value = 'details';
      return;
    } else if (e?.data?.detail === 'Missing payment info.') {
      showPaymentInfoModal.value = true;
      wizardState.value = 'details';
      return;
    }
    console.log(e);
    mintingError.value = e?.message;
    wizardState.value = 'details';
  }
};

const { data, open, close } = useWebSocket(
  config.public.network === 'mainnet'
    ? 'wss://f8i0znw08d.execute-api.eu-central-1.amazonaws.com/wss'
    : 'wss://8esmxw4ejc.execute-api.eu-central-1.amazonaws.com/wss',
  {
    immediate: false,
    autoReconnect: true,
  }
);
watch(data, () => {
  const newEvent = JSON.parse(data.value);
  console.log({ event: newEvent });
  if (newEvent.type === 0 && newEvent.nft_id === mintedNftId.value) {
    mintPending.value = false;
  }
});

const confirmedCollection = ref();

async function checkMintUnwindJob() {
  const { status } = await $fetch<any>(
    `https://${config.public.network === 'mainnet' ? '' : 'testnet.'}transaction-queue.mintgarden.io/jobs/${
      unwindJobId.value
    }`
  );
  if (status === 'completed') {
    mintPending.value = false;
    if (unwindType.value === 'offer') {
      mintOffering.value = true;
      const { collection } = await $fetch<any>(`${getApiRoot()}/mint/offer`, {
        method: 'post',
        body: {
          mint_id: mint.value.id,
        },
      });
      // TODO error handling
      confirmedCollection.value = collection;
      mintOffering.value = false;
    }
  }
}

const { pause, resume } = useTimeoutPoll(checkMintUnwindJob, 5000);

watch(
  mintPending,
  (value) => {
    if (mintType.value === 'edition' || mintType.value === 'series') {
      if (value) {
        resume();
      } else {
        pause();
      }
    } else if (value) {
      open();
    } else {
      close();
    }
  },
  { immediate: true }
);

const openFilePicker = () => {
  document.querySelector<HTMLInputElement>('#dropzoneFileLabel')?.click();
};
</script>
<template>
  <div class="dark:text-white">
    <div v-if="wizardState !== 'type'" aria-hidden="true" class="mb-8 mt-16">
      <div class="overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-700">
        <div class="h-2 rounded-full bg-emerald-600" :style="{ width: getProgressWidth(wizardState) }" />
      </div>
      <div class="mt-3 hidden grid-cols-5 text-sm font-medium text-neutral-600 dark:text-neutral-300 sm:grid">
        <div data-test-id="status-item" class="cursor-pointer" @click="wizardState = 'creator'">Creator</div>
        <div data-test-id="status-item" class="cursor-pointer text-center" @click="wizardState = 'collection'">
          {{ mintType === 'edition' ? 'Edition' : 'Collection' }}
        </div>
        <div data-test-id="status-item" class="cursor-pointer text-center" @click="wizardState = 'data'">Data</div>
        <div data-test-id="status-item" class="cursor-pointer text-center" @click="wizardState = 'details'">
          Details
        </div>
        <div data-test-id="status-item" class="text-right">Mint</div>
      </div>
    </div>

    <div v-if="wizardState === 'type'" class="flex flex-col items-center">
      <div class="mt-4 text-xl">What kind of NFT do you want to create?</div>
      <div class="mt-12 grid w-full gap-8 md:grid-cols-2">
        <button
          class="group flex flex-col items-center justify-center gap-2 rounded-xl border border-neutral-300 px-16 py-12 text-2xl transition duration-100 hover:border-neutral-400 dark:border-neutral-600 dark:hover:border-neutral-500"
          @click="setType('single')"
        >
          <PhotographIcon class="h-16 w-16 text-emerald-600" />
          <span class="font-medium text-neutral-800 dark:text-neutral-100">Single</span>
          <span class="text-base text-neutral-700 dark:text-neutral-200">One of a kind</span>
        </button>
        <button
          class="group flex flex-col items-center justify-center gap-2 rounded-xl border border-neutral-300 px-16 py-12 text-2xl transition duration-100 hover:border-neutral-400 dark:border-neutral-600 dark:hover:border-neutral-500"
          @click="setType('edition')"
        >
          <CollectionIcon class="h-16 w-16 text-emerald-600" />
          <span class="font-medium text-neutral-800 dark:text-neutral-100">Edition</span>
          <span class="text-base text-neutral-700 dark:text-neutral-200">Multiple copies of one work</span>
        </button>
        <!-- <button
          class="group flex flex-col items-center justify-center gap-2 rounded-xl border border-neutral-300 px-16 py-12 text-2xl transition duration-100 hover:border-neutral-400 dark:border-neutral-600 dark:hover:border-neutral-500"
          @click="setType('series')"
        >
          <CollectionIcon class="h-16 w-16 text-emerald-600" />
          <span class="font-medium text-neutral-800 dark:text-neutral-100">Series</span>
          <span class="text-base text-neutral-700 dark:text-neutral-200">Multiple unique works</span>
        </button> -->
      </div>
      <div class="mt-32 text-center text-neutral-700 dark:text-neutral-200">
        Looking for the MintGarden Studio for <span @click="setType('series')">bulk</span> minting?
        <NuxtLink class="font-bold text-emerald-600" to="/mint/studio">Go here</NuxtLink>
        <NuxtLink class="mx-auto flex w-full max-w-md" to="/mint/studio">
          <img src="/mintgarden_studio_screenshot.webp" alt="" />
        </NuxtLink>
      </div>
    </div>
    <div v-else-if="wizardState === 'creator'" class="flex flex-col items-center">
      <div class="mt-4 text-xl">
        Which profile should be assigned as the creator of your {{ mintType === 'edition' ? 'edition' : 'NFT' }}?
      </div>
      <div class="mt-4 flex w-full justify-center gap-8">
        <MintDidSelector v-model="selectedDid" />
      </div>
      <p class="mt-4 text-center text-sm text-neutral-500 dark:text-neutral-400">
        Profiles are tokens on the blockchain, also known as DIDs (Decentralized Identifiers).<br />
        They are used to represent both creators and collectors on-chain.
      </p>
      <p class="mt-4 text-sm text-neutral-500 dark:text-neutral-400">
        New profiles can be created in your
        <NuxtLink class="font-bold" to="/wallet?tab=profiles">Wallet</NuxtLink>.
      </p>
      <button
        v-if="selectedDid"
        class="betterhover:hover:shadow-card-hover mt-8 flex items-center justify-center rounded-full border border-transparent bg-black px-8 py-3 text-base font-medium text-white transition duration-100 ease-in-out dark:bg-white dark:text-black md:text-lg betterhover:hover:-translate-y-0.5"
        @click="wizardState = 'collection'"
      >
        Next
      </button>
    </div>
    <form
      v-else-if="wizardState === 'collection'"
      class="flex h-full flex-col items-center"
      @submit.prevent="wizardState = 'data'"
    >
      <div v-if="mintType === 'single'">
        <div class="mt-4 text-xl">Do you want to group your NFTs in a collection?</div>
        <div class="mt-4">
          <MintCollectionSelector
            v-model="selectedCollection"
            :profile="selectedDid"
            :additional-collections="newCollections"
          />
        </div>
        <div v-if="selectedDid" class="mt-2 text-center">
          <button
            class="text-sm font-semibold text-neutral-700 dark:text-neutral-200"
            @click.prevent="showCreateCollectionModal = true"
          >
            Add new collection
          </button>
        </div>
      </div>
      <div v-else-if="mintType === 'edition'" class="mt-4">
        <div>
          <div class="text-xl">How many pieces should your edition have?</div>
          <RadioGroup v-model="selectedEditionOption" class="mt-4">
            <RadioGroupLabel class="sr-only">Choose the number of editions</RadioGroupLabel>
            <div class="grid grid-cols-4 gap-3">
              <RadioGroupOption
                v-for="option in editionOptions"
                :key="option.name"
                v-slot="{ active, checked }"
                as="template"
                :value="option"
              >
                <div
                  :class="[
                    active ? 'ring-2 ring-emerald-600 ring-offset-2' : '',
                    checked
                      ? 'bg-emerald-600 text-white hover:bg-emerald-500'
                      : 'bg-white text-neutral-700 ring-1 ring-inset ring-neutral-300 hover:bg-neutral-50 dark:bg-black dark:text-neutral-200 dark:ring-neutral-600 dark:hover:bg-neutral-900',
                    'flex cursor-pointer items-center justify-center rounded-md px-3 py-3 text-sm font-semibold sm:flex-1',
                  ]"
                >
                  <RadioGroupLabel as="span">{{ option.name }}</RadioGroupLabel>
                </div>
              </RadioGroupOption>
            </div>
          </RadioGroup>
          <div v-if="selectedEditionOption?.name === 'Custom'" class="relative mt-3">
            <input
              id="editionTotal"
              v-model="editionTotal"
              type="number"
              step="1"
              name="editionTotal"
              min="2"
              max="100"
              class="block w-full rounded-md border-neutral-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 dark:border-neutral-600 dark:bg-neutral-900 dark:text-white sm:text-sm"
            />
          </div>
        </div>
        <div class="mt-12">
          <div class="text-xl">Do you want to include this edition in an existing collection?</div>
          <RadioGroup v-model="shouldGroupEditionIntoCollection" class="mt-4">
            <div class="grid grid-cols-2 gap-3 md:grid-cols-4">
              <div class="hidden md:block"></div>
              <RadioGroupOption
                v-for="option in groupEditionIntoCollectionOptions"
                :key="option.name"
                v-slot="{ active, checked }"
                as="template"
                :value="option.value"
                aria-required="true"
              >
                <div
                  :class="[
                    active ? 'ring-2 ring-emerald-600 ring-offset-2' : '',
                    checked
                      ? 'bg-emerald-600 text-white hover:bg-emerald-500'
                      : 'bg-white text-neutral-700 ring-1 ring-inset ring-neutral-300 hover:bg-neutral-50 dark:bg-black dark:text-neutral-200 dark:ring-neutral-600 dark:hover:bg-neutral-900',
                    'flex cursor-pointer items-center justify-center rounded-md px-3 py-3 text-sm font-semibold sm:flex-1',
                  ]"
                >
                  <RadioGroupLabel as="span">{{ option.name }}</RadioGroupLabel>
                </div>
              </RadioGroupOption>
            </div>
          </RadioGroup>
        </div>
        <div v-if="shouldGroupEditionIntoCollection === false" class="mt-8">
          <div class="text-xl">Fill in the collection details of your edition</div>
          <p class="my-2 text-sm text-neutral-500 dark:text-neutral-400">
            A collection groups all NFTs of your edition together.
          </p>

          <GobyCreateCollectionForm v-model="newEdition" :profile-id="selectedDid.id" />
        </div>
        <div v-else-if="shouldGroupEditionIntoCollection === true" class="mt-4">
          <MintCollectionSelector v-model="selectedCollection" :profile="selectedDid" :required="true" />
        </div>
      </div>
      <div v-else-if="mintType === 'series'" class="mt-4">
        <div class="text-xl">The collection will be picked up from your metadata</div>
      </div>
      <button
        v-if="
          mintType === 'single' ||
          mintType === 'series' ||
          (editionTotal && (shouldGroupEditionIntoCollection === false || selectedCollection))
        "
        type="submit"
        class="betterhover:hover:shadow-card-hover mt-8 flex items-center justify-center rounded-full border border-transparent bg-black px-8 py-3 text-base font-medium text-white transition duration-100 ease-in-out dark:bg-white dark:text-black md:text-lg betterhover:hover:-translate-y-0.5"
      >
        Next
      </button>
    </form>
    <div v-show="wizardState === 'data'" class="flex h-full flex-col items-center">
      <MintFolderUploader v-if="mintType == 'series'" v-model="seriesData" />

      <template v-else>
        <div class="mt-4 text-xl">What image or video do you want to mint?</div>
        <div
          v-if="currentFile"
          class="relative mt-4 block w-full max-w-screen-lg cursor-pointer rounded-lg border-2 border-dashed border-neutral-300 p-6 text-center hover:border-neutral-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 dark:border-neutral-600"
        >
          <div
            class="mx-auto mt-1 flex max-w-md cursor-pointer justify-center rounded-lg drop-shadow-[0_4px_20px_rgba(0,0,0,0.25)]"
          >
            <video
              v-if="isVideo(currentFile.name)"
              :src="currentFile.objectUrl"
              class="rounded-lg"
              @click="openFilePicker()"
            />
            <img
              v-else
              alt="Image to upload"
              :src="currentFile.objectUrl"
              class="rounded-lg"
              @click="openFilePicker()"
            />
          </div>
          <div class="mt-4 flex flex-grow items-center justify-center">
            <button
              class="flex justify-center rounded-full border border-transparent px-4 py-3 font-semibold transition duration-100 ease-in-out"
              @click="resetFile()"
            >
              Remove
            </button>
          </div>
        </div>
        <div v-show="!currentFile" class="mt-4 w-full max-w-screen-lg">
          <MintDropZone ref="dropzone" @update:model-value="loadAndSetFile" />
        </div>
      </template>
      <button
        class="betterhover:hover:shadow-card-hover mt-8 flex items-center justify-center rounded-full border border-transparent bg-black px-8 py-3 text-base font-medium text-white transition duration-100 ease-in-out dark:bg-white dark:text-black md:text-lg betterhover:hover:-translate-y-0.5"
        @click="mintType === 'series' ? handleSeriesUpload() : currentFile && (wizardState = 'details')"
      >
        Next
      </button>
    </div>
    <form v-show="wizardState === 'details'" class="w-full space-y-8" @submit.prevent="uploadAndMintNft">
      <div v-if="nftData" class="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-6">
        <div class="col-span-6 space-y-6 md:col-span-3">
          <MintSingleDetailsForm
            v-model:metadata="nftData.metadata"
            v-model:on-chain-metadata="onChainMetadata"
            :mint-type="mintType"
            :license-from-collection="licenseFromCollection"
          />

          <div>
            <label for="royaltyAddress" class="block text-sm font-medium text-neutral-700 dark:text-neutral-200">
              Royalty Address
            </label>
            <div class="mt-1">
              <input
                id="royaltyAddress"
                v-model="onChainMetadata.royaltyAddress"
                type="text"
                name="royaltyAddress"
                autocomplete="off"
                data-1p-ignore
                placeholder="Will use your wallet address if left empty"
                class="block w-full rounded-md border-neutral-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 dark:border-neutral-600 dark:bg-neutral-900 dark:text-white sm:text-sm"
              />
            </div>
          </div>

          <div v-if="mintType !== 'single'">
            <RadioGroup v-model="selectedMintingType">
              <RadioGroupLabel class="text-sm font-medium text-neutral-700 dark:text-neutral-200"
                >Minting Method
              </RadioGroupLabel>

              <div class="mt-1 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4">
                <RadioGroupOption
                  v-for="mintingType in mintingTypes"
                  :key="mintingType.id"
                  v-slot="{ active, checked }"
                  as="template"
                  :value="mintingType"
                  :disabled="mintingType.disabled"
                >
                  <div
                    :class="[
                      active
                        ? 'border-emerald-600 ring-2 ring-emerald-600'
                        : mintingType.disabled
                        ? 'border-neutral-200 dark:border-neutral-700'
                        : 'border-neutral-300 dark:border-neutral-600',
                      mintingType.disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                      'relative flex rounded-lg border bg-white p-4 shadow-sm focus:outline-none dark:bg-black',
                    ]"
                  >
                    <span class="flex flex-1">
                      <span class="flex flex-col">
                        <RadioGroupLabel
                          as="span"
                          :class="[
                            mintingType.disabled
                              ? 'text-neutral-300 dark:text-neutral-600'
                              : 'text-neutral-900 dark:text-neutral-50',
                          ]"
                          class="block text-sm font-medium"
                          >{{ mintingType.title }}</RadioGroupLabel
                        >
                        <RadioGroupDescription
                          as="span"
                          class="mt-1 flex flex-1 items-center text-sm"
                          :class="[
                            mintingType.disabled
                              ? 'text-neutral-300 dark:text-neutral-600'
                              : 'text-neutral-500 dark:text-neutral-400',
                          ]"
                          >{{ mintingType.description }}</RadioGroupDescription
                        >
                        <RadioGroupDescription
                          as="span"
                          :class="[
                            mintingType.disabled
                              ? 'text-neutral-300 dark:text-neutral-600'
                              : 'text-neutral-900 dark:text-neutral-50',
                          ]"
                          class="mt-6 text-sm font-medium"
                          >{{ mintingType.users }}</RadioGroupDescription
                        >
                      </span>
                    </span>
                    <CheckCircleIcon
                      :class="[!checked ? 'invisible' : '', 'h-5 w-5 text-emerald-600']"
                      aria-hidden="true"
                    />
                    <span
                      :class="[
                        active ? 'border' : 'border-2',
                        checked ? 'border-emerald-600' : 'border-transparent',
                        'pointer-events-none absolute -inset-px rounded-lg',
                      ]"
                      aria-hidden="true"
                    />
                  </div>
                </RadioGroupOption>
              </div>
            </RadioGroup>
          </div>
          <template v-if="mintType === 'series'">
            <div v-if="selectedMintingType.id === 'on-demand'">
              <label for="name" class="block text-sm font-medium text-neutral-700 dark:text-neutral-200">
                Mint Title
              </label>
              <div class="mt-1">
                <input
                  id="name"
                  v-model="seriesMetadata.mint_name"
                  type="text"
                  name="name"
                  required
                  class="block w-full rounded-md border-neutral-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 dark:border-neutral-600 dark:bg-neutral-900 dark:text-white sm:text-sm"
                />
              </div>
              <p class="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
                This will be shown to users that want to mint your NFT on MintGarden. It doesn't go on chain.
              </p>
            </div>

            <div v-if="selectedMintingType.id === 'on-demand'">
              <label for="description" class="block text-sm font-medium text-neutral-700 dark:text-neutral-200">
                Mint Description
              </label>
              <div class="mt-1">
                <textarea
                  id="description"
                  v-model="seriesMetadata.mint_description"
                  name="description"
                  rows="5"
                  class="block w-full rounded-md border border-neutral-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 dark:border-neutral-600 dark:bg-neutral-900 dark:text-white sm:text-sm"
                />
              </div>
            </div>

            <div v-if="selectedMintingType.id === 'on-demand'">
              <label for="thumbnail_uri" class="block text-sm font-medium text-neutral-700 dark:text-neutral-200">
                Mint Thumbnail URL
              </label>
              <div class="mt-1">
                <input
                  id="thumbnail_uri"
                  v-model="seriesMetadata.mint_thumbnail_uri"
                  type="text"
                  thumbnail_uri="thumbnail_uri"
                  required
                  class="block w-full rounded-md border-neutral-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 dark:border-neutral-600 dark:bg-neutral-900 dark:text-white sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label for="targetAddress" class="block text-sm font-medium text-neutral-700 dark:text-neutral-200">
                {{ selectedMintingType.id === 'on-demand' ? 'Primary Sale Address' : 'Target Address' }}
              </label>
              <div class="mt-1">
                <input
                  id="targetAddress"
                  v-model="onChainMetadata.targetAddress"
                  type="text"
                  name="targetAddress"
                  autocomplete="off"
                  data-1p-ignore
                  placeholder="Will use your wallet address if left empty"
                  class="block w-full rounded-md border-neutral-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 dark:border-neutral-600 dark:bg-neutral-900 dark:text-white sm:text-sm"
                />
              </div>
            </div>
          </template>

          <div v-if="mintType !== 'single' && selectedMintingType.id === 'on-demand'">
            <div>
              <label for="name" class="block text-sm font-medium text-neutral-700 dark:text-neutral-200">
                Sale Price
              </label>
              <div class="mt-1 flex gap-2">
                <input
                  id="offerPrice"
                  v-model="offerPrice"
                  type="number"
                  step="any"
                  name="offerPrice"
                  :min="offerRequestedTokenId && offerRequestedTokenId !== 'xch' ? 0.001 : 0.000000000001"
                  class="block flex-1 rounded-md border-neutral-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 dark:border-neutral-600 dark:bg-neutral-900 dark:text-white sm:text-sm"
                />
                <TokenPicker v-model="offerRequestedTokenId" />
              </div>
            </div>
          </div>
          <div v-if="selectedMintingType.id === 'on-demand' && mintType === 'series'">
            <RadioGroup v-model="selectedPublishingMethod">
              <RadioGroupLabel class="text-sm font-medium text-neutral-700 dark:text-neutral-200"
                >Publishing Method
              </RadioGroupLabel>

              <div class="mt-1 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4">
                <RadioGroupOption
                  v-for="publishingMethod in publishingMethods"
                  :key="publishingMethod.id"
                  v-slot="{ active, checked }"
                  as="template"
                  :value="publishingMethod"
                >
                  <div
                    :class="[
                      active
                        ? 'border-emerald-600 ring-2 ring-emerald-600'
                        : 'border-neutral-300 dark:border-neutral-600',
                      'relative flex cursor-pointer rounded-lg border bg-white p-4 shadow-sm focus:outline-none dark:bg-black',
                    ]"
                  >
                    <span class="flex flex-1">
                      <span class="flex flex-col">
                        <RadioGroupLabel
                          as="span"
                          class="block text-sm font-medium text-neutral-900 dark:text-neutral-50"
                          >{{ publishingMethod.title }}</RadioGroupLabel
                        >
                        <RadioGroupDescription
                          as="span"
                          class="mt-1 flex flex-1 items-center text-sm text-neutral-500 dark:text-neutral-400"
                          >{{ publishingMethod.description }}</RadioGroupDescription
                        >
                      </span>
                    </span>
                    <CheckCircleIcon
                      :class="[!checked ? 'invisible' : '', 'h-5 w-5 text-emerald-600']"
                      aria-hidden="true"
                    />
                    <span
                      :class="[
                        active ? 'border' : 'border-2',
                        checked ? 'border-emerald-600' : 'border-transparent',
                        'pointer-events-none absolute -inset-px rounded-lg',
                      ]"
                      aria-hidden="true"
                    />
                  </div>
                </RadioGroupOption>
              </div>
            </RadioGroup>
          </div>
          <div
            v-if="
              selectedMintingType.id === 'on-demand' &&
              selectedPublishingMethod.id === 'private' &&
              mintType === 'series'
            "
          >
            <label for="name" class="block text-sm font-medium text-neutral-700 dark:text-neutral-200">
              Release Date
            </label>
            <div class="mt-1 flex gap-2">
              <Popover>
                <PopoverTrigger as-child>
                  <Button
                    :variant="'outline'"
                    :class="
                      cn(
                        'w-full justify-start text-left font-normal dark:border-neutral-700 dark:bg-neutral-900 dark:text-white'
                      )
                    "
                  >
                    <CalendarIcon class="mr-2 h-4 w-4" />
                    <span>{{
                      seriesMetadata.release_time ? format(seriesMetadata.release_time, 'PPP - ppp') : 'Pick a date'
                    }}</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent class="w-auto p-0">
                  <Calendar v-model="seriesMetadata.release_time" mode="datetime" />
                </PopoverContent>
              </Popover>
              <Button
                v-if="seriesMetadata.release_time"
                variant="outline"
                @click="seriesMetadata.release_time = undefined"
              >
                Clear
              </Button>
            </div>
          </div>
          <div class="relative flex items-start">
            <div class="flex h-5 items-center">
              <input
                id="confirmLegal"
                v-model="confirmLegal"
                required
                name="confirmLegal"
                type="checkbox"
                class="h-4 w-4 rounded border-neutral-300 text-emerald-600 focus:ring-emerald-500 dark:bg-neutral-900"
              />
            </div>
            <div class="ml-3 text-sm">
              <label for="confirmLegal" class="font-medium text-neutral-700 dark:text-neutral-200"
                >I agree to the
                <NuxtLink class="font-semibold text-emerald-600" to="/mint/terms-of-service" target="_blank"
                  >Terms of Service
                </NuxtLink>
                for MintGarden Studio.</label
              >
            </div>
          </div>
          <div v-if="mintingError" class="rounded-md bg-red-50 p-4 dark:bg-red-950">
            <div class="flex">
              <div class="flex-shrink-0">
                <XCircleIcon class="h-5 w-5 text-red-400 dark:text-red-500" aria-hidden="true" />
              </div>
              <div class="ml-3">
                <h3 class="text-sm font-medium text-red-800 dark:text-red-200">
                  There was an error while minting your NFT
                </h3>
                <div class="mt-2 text-sm text-red-700 dark:text-red-300">
                  <ul role="list" class="list-disc space-y-1 pl-5">
                    <li>{{ mintingError }}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-span-6 space-y-6 md:col-span-2 md:col-start-5">
          <NftCard v-if="mintType !== 'series'" :nft="nftForPreview" :unlinked="true" :always-show-text="true" />
          <MintCard v-else-if="selectedMintingType.id === 'on-demand'" :mint="mintForPreview" />
          <CollectionCard
            v-else-if="selectedMintingType.id === 'direct'"
            :collection="collectionForPreview"
            :unlinked="true"
          />
        </div>
      </div>

      <div class="pt-5">
        <div class="flex justify-center">
          <button
            type="submit"
            class="betterhover:hover:shadow-card-hover mt-4 flex items-center justify-center rounded-full border border-transparent bg-black px-8 py-3 text-base font-medium text-white transition duration-100 ease-in-out dark:bg-white dark:text-black md:text-lg betterhover:hover:-translate-y-0.5"
          >
            Mint NFT
          </button>
        </div>
      </div>
    </form>
    <div v-show="wizardState === 'mint'" class="flex flex-col items-center">
      <div class="mt-4 grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-6">
        <div class="col-span-6 space-y-6 md:col-span-3">
          <div class="text-4xl">Minting</div>
          <ol role="list" class="mt-8 overflow-hidden">
            <li
              v-for="(step, stepIdx) in mintingSteps"
              :key="step.name"
              :class="[stepIdx !== mintingSteps.length - 1 ? 'pb-10' : '', 'relative']"
            >
              <template v-if="step.status === 'complete'">
                <div
                  v-if="stepIdx !== mintingSteps.length - 1"
                  class="absolute left-4 top-4 -ml-px mt-0.5 h-full w-0.5 bg-emerald-600"
                  aria-hidden="true"
                />
                <a class="group relative flex items-start">
                  <span class="flex h-9 items-center">
                    <span class="relative z-10 flex h-8 w-8 items-center justify-center rounded-full bg-emerald-600">
                      <CheckIcon class="h-5 w-5 text-white dark:text-black" aria-hidden="true" />
                    </span>
                  </span>
                  <span class="ml-4 flex min-w-0 flex-col">
                    <span class="text-sm font-medium">{{ step.name }}</span>
                    <span class="text-sm text-neutral-500 dark:text-neutral-400">{{ step.description }}</span>
                  </span>
                </a>
              </template>
              <template v-else-if="step.status === 'current'">
                <div
                  v-if="stepIdx !== mintingSteps.length - 1"
                  class="absolute left-4 top-4 -ml-px mt-0.5 h-full w-0.5 bg-neutral-300 dark:bg-neutral-600"
                  aria-hidden="true"
                />
                <a class="group relative flex items-start" aria-current="step">
                  <span class="flex h-9 items-center" aria-hidden="true">
                    <span
                      class="relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 border-emerald-600 bg-white dark:bg-black"
                    >
                      <span class="absolute h-2.5 w-2.5 rounded-full bg-emerald-600" />
                      <span class="h-2.5 w-2.5 animate-ping rounded-full bg-emerald-600" />
                    </span>
                  </span>
                  <span class="ml-4 flex min-w-0 flex-col">
                    <span class="text-sm font-medium text-emerald-600">{{ step.name }}</span>
                    <span class="text-sm text-neutral-500 dark:text-neutral-400">{{ step.description }}</span>
                    <span
                      v-if="step.name === 'Uploading files'"
                      class="mt-2 block overflow-hidden rounded-full bg-gray-200"
                    >
                      <span
                        class="block h-2 rounded-full bg-emerald-600"
                        :style="{ width: uploadProgress * 100 + '%' }"
                      ></span>
                    </span>
                  </span>
                </a>
              </template>
              <template v-else>
                <div
                  v-if="stepIdx !== mintingSteps.length - 1"
                  class="absolute left-4 top-4 -ml-px mt-0.5 h-full w-0.5 bg-neutral-300 dark:bg-neutral-600"
                  aria-hidden="true"
                />
                <a class="group relative flex items-start">
                  <span class="flex h-9 items-center" aria-hidden="true">
                    <span
                      class="relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 border-neutral-300 bg-white group-hover:border-neutral-400 dark:bg-black"
                    >
                      <span
                        class="h-2.5 w-2.5 rounded-full bg-transparent group-hover:bg-neutral-300 dark:group-hover:bg-neutral-600"
                      />
                    </span>
                  </span>
                  <span class="ml-4 flex min-w-0 flex-col">
                    <span class="text-sm font-medium text-neutral-500 dark:text-neutral-400">{{ step.name }}</span>
                    <span class="text-sm text-neutral-500 dark:text-neutral-400">{{ step.description }}</span>
                  </span>
                </a>
              </template>
            </li>
          </ol>
        </div>

        <div class="col-span-6 space-y-6 md:col-span-2 md:col-start-5">
          <NftCard v-if="mintType === 'single'" :nft="nftForPreview" :unlinked="mintPending" />
          <MintCard
            v-else-if="mintType === 'series' && selectedMintingType.id === 'on-demand'"
            :mint="mintForPreview"
          />
          <CollectionCard
            v-else-if="collectionForPreview"
            :collection="collectionForPreview"
            :unlinked="mintPending || mintOffering"
          />
        </div>
      </div>
    </div>
    <GobyCreateCollectionModal
      v-if="selectedDid"
      v-model="showCreateCollectionModal"
      :profile-id="selectedDid.id"
      @on-save="addCollection($event)"
    />
    <GobyPaymentInfoModal v-model="showPaymentInfoModal" @on-save="setPaymentInfoAndMint($event)" />
  </div>
</template>

<style>
input[type='number']::-webkit-outer-spin-button,
input[type='number']::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type='number'] {
  -moz-appearance: textfield;
}
</style>
